import { useAuth } from "@clerk/clerk-react";
import QueryString from "qs";
import useSWR from "swr";
import { TNotification } from "../types/Notifications";
import { apiPatch, apiPost, fetcher } from "../utils/axios";
import { DefaultFilterType } from "../types/PageFilterTypes";

const useNotifications = (
  defaultFilter: DefaultFilterType & {
    device_id?: string;
    is_read?: boolean;
  } = {
    device_id: "",
    is_read: true,
    limit: 20,
    sort: "updated_at|desc",
  }
) => {
  const { getToken } = useAuth();
  const { data, error, mutate } = useSWR(
    `/notifications?${QueryString.stringify(defaultFilter)}`,
    async (url) => {
      const token = await getToken();
      return fetcher(url, token);
    },
    { refreshInterval: 60000 }
  );

  const updateNotification = async (id: string, is_read: boolean) => {
    const token = await getToken();
    const notification = await apiPatch(
      `/notifications/${id}`,
      { is_read },
      token
    );
    mutate();
    return notification;
  };

  const markAllAsRead = async () => {
    const token = await getToken();
    await apiPost(`/notifications/mark-all-as-read`, {}, token);
    mutate();
  };

  return {
    data: data?.data as TNotification[] | [],
    pagination: (data?.pagination as any) || {},
    isLoading: !error && !data,
    isError: error,
    updateNotification,
    mutate,
    markAllAsRead,
  };
};

const useNotification = (id: string) => {
  const { getToken } = useAuth();
  const { data, error } = useSWR(`/notifications/${id}`, async (url) => {
    const token = await getToken();
    return fetcher(url, token);
  });

  return {
    notification: data as TNotification | undefined,
    isLoading: !error && !data,
    isError: error,
  };
};

export { useNotification, useNotifications };
